<template>
  <ModalWrapper
    class="login-modal"
    show-on-action-intent="login"
    :disable-close="false"
    component="LoginModal"
    :blok="{ ...blok, bgVariant: 'white' }"
    @modal-closed="reset()"
  >
    <h2
      class="title -mx-6 -mt-12 flex items-center gap-2 border-b px-4 pb-2 text-2xl leading-none text-gray-900 lg:-mx-4 lg:-mt-6 lg:text-xl"
    >
      <UserCircleIcon class="text-default h-8 w-8 lg:h-6 lg:w-6" />
      Log in of Registreer
    </h2>

    <form novalidate class="mt-8" @submit="handleNextStep">
      <div v-if="!otpSent">
        <p class="mb-4 text-gray-900">
          Vul je e-mailadres in om in te loggen bij {{ store.site.name }}.
        </p>

        <label
          for="login-email"
          class="mt-8 block text-sm font-medium text-gray-900"
          >E-mail</label
        >
        <div class="relative mt-1 rounded-md">
          <input
            id="login-email"
            v-model.trim="state.email"
            type="email"
            class="focus-default mt-1 block w-full rounded-md shadow-xs focus:border-transparent sm:text-sm"
            :class="{
              'border-red-500 pr-10 text-red-900 focus:border-red-500 focus:outline-2 focus:outline-red-500':
                v$.email.$error,
              'border-gray-300': !v$.email.$error,
            }"
            placeholder="voorbeeld@provider.nl"
            autocomplete="email"
            required
            :aria-invalid="v$.email?.$error"
            :aria-describedby="v$.email?.$error ? 'login-email-error' : null"
          />
          <div
            v-if="v$.email.$error"
            class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
          >
            <ExclamationCircleIcon class="h-5 w-5 text-red-500" />
          </div>
        </div>
        <div
          id="login-email-error"
          class="mt-0.5 min-h-[2.5rem] text-sm text-red-600"
        >
          <p v-if="v$.email.$error" role="alert">
            {{ v$.$errors[0].$message }}
          </p>
          <p v-else-if="authError">{{ authError }}</p>
        </div>
      </div>
      <div v-else>
        <p class="text-gray-900">
          Er is een code naar u verzonden op
          <span class="font-bold">{{ state.email }}</span>
        </p>

        <div class="mt-2 mb-4 text-sm text-gray-700">
          <span v-if="timeBeforeResend > 0"
            >Opnieuw verzenden beschikbaar in
            <span class="font-weight-bold"
              >00:{{ timeBeforeResend.toString().padStart(2, '0') }}</span
            ></span
          >
          <a v-else class="underline" href="#" @click="resendOTP"
            >Verstuur code opnieuw</a
          >
        </div>

        <label
          for="login-code"
          class="mt-8 block text-sm font-medium text-gray-900"
          >Code</label
        >
        <div class="relative mt-1 rounded-md">
          <input
            id="login-code"
            v-model.trim="state.code"
            type="text"
            class="focus-default mt-1 block w-full rounded-md shadow-xs focus:border-transparent sm:text-sm"
            :class="{
              'border-red-500 pr-10 text-red-900 focus:border-red-500 focus:outline-2 focus:outline-red-500':
                v$.code.$error,
              'border-gray-300': !v$.code.$error,
            }"
            autocomplete="none"
            required
            :aria-invalid="v$.code?.$error"
            :aria-describedby="v$.code?.$error ? 'login-code-error' : null"
          />
          <div
            v-if="v$.code.$error"
            class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
          >
            <ExclamationCircleIcon class="h-5 w-5 text-red-500" />
          </div>
        </div>
        <div
          id="login-code-error"
          class="mt-0.5 min-h-[2.5rem] text-sm text-red-600"
        >
          <p v-if="v$.code.$error" role="alert">
            {{ v$.$errors[0].$message }}
          </p>
          <p v-else-if="authError">{{ authError }}</p>
        </div>
      </div>

      <div
        class="-mx-6 mt-2 flex flex-col items-center border-t px-4 pt-4 lg:-mx-4"
      >
        <button
          id="button-login"
          type="submit"
          class="btn-style-default group inline-flex min-h-[2.6rem] w-full items-center justify-center px-4 py-2 text-base font-medium"
        >
          <template v-if="isLoading">
            <IconSpinner aria-hidden="true" />
            <span class="sr-only">Bezig</span>
          </template>
          <span v-else> Doorgan</span>
        </button>
        <button
          v-if="otpSent"
          type="button"
          class="group inline-flex w-fit items-center justify-center px-4 pt-2 text-sm font-medium text-gray-900 focus:underline focus:outline-hidden"
          @click="goToPreviousStep()"
        >
          Verander e-mail
        </button>
      </div>
    </form>
  </ModalWrapper>
</template>

<script setup>
import { reactive, ref, watch } from 'vue'
import ModalWrapper from './ModalWrapper.vue'
import useVuelidate from '@vuelidate/core'
import { email, helpers, required, requiredIf } from '@vuelidate/validators'
import { useMainStore } from '../store'
import { UserCircleIcon } from '@heroicons/vue/24/solid'
import { ExclamationCircleIcon } from '@heroicons/vue/20/solid'
import { useHead } from '@unhead/vue'
import { usePageContext } from '../renderer/usePageContext'
import { useColorPalette } from '../composables/colorPalette'
import IconSpinner from '../icons/IconSpinner.vue'

const props = defineProps({
  blok: {
    type: Object,
    default: () => {},
  },
})

const store = useMainStore()
const { colorCustomClass } = useColorPalette()

const isLoading = ref(false)
const resendTimerEnabled = ref(false)
const timeBeforeResend = ref(15)
const authError = ref('')
const otpSent = ref(false)

const state = reactive({
  email: null,
  code: null,
})
const rules = {
  email: {
    required: helpers.withMessage(
      'Het veld e-mailadres is niet ingevuld. Vul hier je e-mailadres in.',
      required,
    ),
    email: helpers.withMessage(
      'Dit is geen juist e-mailadres. Vul hier een geldig e-mailadres in, bijvoorbeeld naam@gmail.com',
      email,
    ),
  },
  code: {
    required: helpers.withMessage(
      'Vul hier de code in die je hebt ontvangen',
      requiredIf(otpSent),
    ),
  },
}
const v$ = useVuelidate(rules, state)

const handleNextStep = (event) => {
  // Prevent modal from closing
  event.preventDefault()

  if (isLoading.value) return

  v$.value.$touch()
  if (!v$.value.$invalid) {
    if (!otpSent.value) {
      sendOTP()
    } else {
      validateOTP()
    }
  }
}

const goToPreviousStep = () => {
  otpSent.value = false
  resendTimerEnabled.value = false
  state.code = ''
  authError.value = ''
  v$.value.$reset()
}

const sendOTP = () => {
  try {
    isLoading.value = true
    store.auth.passwordlessStart(
      {
        connection: 'email',
        send: 'code',
        email: state.email,
      },
      (err) => {
        if (err) {
          authError.value =
            err.description || 'Er is iets fout gegaan. Probeer het opnieuw.'
        } else {
          otpSent.value = true
          resendTimerEnabled.value = true
          v$.value.$reset()
        }

        isLoading.value = false
      },
    )
  } catch (err) {
    authError.value =
      err.description || 'Er is iets fout gegaan. Probeer het opnieuw.'
    isLoading.value = false
  }
}

const validateOTP = () => {
  try {
    isLoading.value = true
    store.auth.passwordlessLogin(
      {
        connection: 'email',
        email: state.email,
        verificationCode: state.code,
      },
      (err) => {
        if (err) {
          if (err.code === 'access_denied') {
            if (err.description === 'Wrong email or verification code.') {
              authError.value = 'Verkeerde code, probeer het opnieuw.'
            } else if (
              err.description.includes('The verification code has expired')
            ) {
              authError.value =
                'Deze code is helaas verlopen. Klik hierboven op "Verstuur code opnieuw" en probeer het opnieuw.'
            } else if (
              err.description.includes('reached the maximum number of attempts')
            ) {
              authError.value =
                'Je hebt het maximale aantal pogingen bereikt. Klik hierboven op "Verstuur code opnieuw" en probeer het opnieuw.'
            } else {
              authError.value = 'Inloggen mislukt'
              window.location.assign(window.location.origin)
            }
          } else {
            authError.value = 'Inloggen mislukt'
            window.location.assign(window.location.origin)
          }
        }

        isLoading.value = false
      },
    )
  } catch (err) {
    authError.value =
      err.description || 'Er is iets fout gegaan. Probeer het opnieuw.'
    isLoading.value = false
  }
}

const resendOTP = () => {
  state.code = ''
  v$.value.$reset()
  authError.value = ''

  sendOTP()
}

const reset = () => {
  otpSent.value = false
  state.email = ''
  state.code = ''
  authError.value = ''
  resendTimerEnabled.value = false
  v$.value.$reset()
}

watch(resendTimerEnabled, (enabled) => {
  if (enabled) {
    timeBeforeResend.value = 15

    const countdown = () => {
      setTimeout(() => {
        timeBeforeResend.value -= 1
        if (!timeBeforeResend.value) {
          resendTimerEnabled.value = false
        } else {
          countdown()
        }
      }, 1000)
    }
    countdown()
  }
})

const pageContext = usePageContext()
useHead(
  () => {
    const buttonColor = 'default'
    // We need to set CSS vars inside <head><style> instead of using v-bind() directly inside <style scoped>
    // because v-bind generates style="..." and that's not allowed with CSP enabled.
    const style = [
      {
        key: props.blok._uid,
        nonce: pageContext.nonce,
        innerHTML: `
          #button-login {
            --btn-color: var(--${colorCustomClass(buttonColor, null, '')});
            --btn-text: var(--${colorCustomClass(null, buttonColor, '')});
            --btn-border: var(--${colorCustomClass('transparent', buttonColor, '')});
          }
      `,
      },
    ]

    return {
      style,
    }
  },
  { mode: 'client' },
)
</script>

<style scoped>
@media (min-width: 576px) {
  .login-modal :deep(.sm\:max-w-lg) {
    max-width: 24rem;
  }
}
</style>
